import {spotheroGet} from 'api';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import {type RawApiResponse} from 'api/api.types';

/**
 * A driver-feedback review prompt: a selectable tag option offered to drivers
 * after a reservation (e.g. "Clean facility" ✨).
 */
export interface ReviewPrompt {
    id: number;
    promptText: string;
    starRating: number;
    active: boolean;
    emoji: string;
}

/**
 * Raw snake_case shape returned by the API for a single review prompt.
 */
export type RawReviewPrompt = RawApiResponse<ReviewPrompt>;

/**
 * Query parameters accepted by GET /api/v1/reviews/prompts/.
 */
export interface GetReviewPromptsParams {
    active?: boolean;
    distinct?: 'prompt_text';
    [key: string]: string | number | boolean | undefined;
}

/**
 * Formats the request params for GET /api/v1/reviews/prompts/.
 *
 * @memberof module:api/facilities/dto
 */
export const formatGetReviewPromptsRequest = (
    params: GetReviewPromptsParams
) => {
    const formattedRequest = convertKeysToSnakeCase(params);

    return formattedRequest;
};

/**
 * Formats the response from GET /api/v1/reviews/prompts/.
 *
 * @memberof module:api/facilities/dto
 */
export const formatGetReviewPromptsResponse = (
    results: RawReviewPrompt[]
): ReviewPrompt[] => {
    const formattedResponse = camelizeKeys(results);

    return formattedResponse;
};

/**
 * Gets the list of driver-feedback review prompts.
 *
 * Defaults to active prompts, distinct by `prompt_text` — the shape needed to
 * populate the driver-feedback filter drawer's "Driver Tags" multiselect.
 *
 * @see '/api/v1/reviews/prompts/'
 */
const getReviewPrompts = async (
    params: GetReviewPromptsParams = {active: true, distinct: 'prompt_text'}
): Promise<ReviewPrompt[]> => {
    try {
        const response = await spotheroGet<RawReviewPrompt[]>(
            'reviews/prompts/',
            formatGetReviewPromptsRequest(params)
        );
        const formattedResponse = formatGetReviewPromptsResponse(
            response.data.data.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getReviewPrompts;
